import React from 'react';
import { OperatorId } from '../../types';

export type PaymentMethodId = 'kpay' | 'wavepay' | 'kbzpay' | 'cbpay' | 'direct_kpay' | 'direct_wave' | 'external_kpay' | 'external_wave' | string;

export interface OperatorBrandConfig {
  id: OperatorId;
  name: string;
  logo: string;
  primaryColor: string;
  badgeBg: string;
  badgeText: string;
  borderColor: string;
  selectedRing: string;
  accentBg: string;
}

export const OPERATOR_CONFIGS: Record<OperatorId, OperatorBrandConfig> = {
  mpt: {
    id: 'mpt',
    name: 'MPT',
    logo: '/logos/mpt.png',
    primaryColor: '#0055A5',
    badgeBg: 'bg-yellow-500/20',
    badgeText: 'text-yellow-300',
    borderColor: 'border-yellow-500/30',
    selectedRing: 'ring-yellow-400/40 border-yellow-500 shadow-yellow-500/10',
    accentBg: 'bg-yellow-500/10',
  },
  atom: {
    id: 'atom',
    name: 'ATOM',
    logo: '/logos/atom.png',
    primaryColor: '#0047FF',
    badgeBg: 'bg-blue-500/20',
    badgeText: 'text-blue-300',
    borderColor: 'border-blue-500/30',
    selectedRing: 'ring-blue-400/40 border-blue-500 shadow-blue-500/10',
    accentBg: 'bg-blue-500/10',
  },
  u9: {
    id: 'u9',
    name: 'U9 (Ooredoo)',
    logo: '/logos/u9.png',
    primaryColor: '#D32F2F',
    badgeBg: 'bg-amber-500/20',
    badgeText: 'text-amber-300',
    borderColor: 'border-amber-500/30',
    selectedRing: 'ring-amber-400/40 border-amber-500 shadow-amber-500/10',
    accentBg: 'bg-amber-500/10',
  },
  mytel: {
    id: 'mytel',
    name: 'Mytel',
    logo: '/logos/mytel.png',
    primaryColor: '#FF6700',
    badgeBg: 'bg-orange-500/20',
    badgeText: 'text-orange-300',
    borderColor: 'border-orange-500/30',
    selectedRing: 'ring-orange-400/40 border-orange-500 shadow-orange-500/10',
    accentBg: 'bg-orange-500/10',
  },
};

const SIZE_MAP = {
  xs: 'w-5 h-5 rounded-md',
  sm: 'w-7 h-7 rounded-lg',
  md: 'w-10 h-10 rounded-xl',
  lg: 'w-12 h-12 rounded-2xl',
  xl: 'w-16 h-16 rounded-2xl',
};

interface OperatorLogoProps {
  id?: OperatorId | string | null;
  size?: keyof typeof SIZE_MAP;
  className?: string;
  showBorder?: boolean;
}

export const OperatorLogo: React.FC<OperatorLogoProps> = ({
  id,
  size = 'md',
  className = '',
  showBorder = true,
}) => {
  const opKey = (id ? String(id).toLowerCase() : 'mpt') as OperatorId;
  const config = OPERATOR_CONFIGS[opKey] || OPERATOR_CONFIGS.mpt;
  const sizeClasses = SIZE_MAP[size] || SIZE_MAP.md;

  return (
    <div
      className={`inline-flex items-center justify-center shrink-0 overflow-hidden bg-white ${
        showBorder ? 'border border-plum-border/60 shadow-xs' : ''
      } ${sizeClasses} ${className}`}
    >
      <img
        src={config.logo}
        alt={`${config.name} logo`}
        className="w-full h-full object-contain p-0.5"
        decoding="async"
        onError={(e) => {
          const target = e.currentTarget;
          target.style.display = 'none';
          if (target.parentElement) {
            target.parentElement.innerText = config.name.slice(0, 3);
          }
        }}
      />
    </div>
  );
};

export interface PaymentBrandConfig {
  name: string;
  logo: string;
  color: string;
  accentBg: string;
  borderColor: string;
  selectedRing: string;
}

export const PAYMENT_CONFIGS: Record<string, PaymentBrandConfig> = {
  kpay: {
    name: 'KBZPay',
    logo: '/logos/kpay.png',
    color: '#0054A6',
    accentBg: 'bg-blue-500/15',
    borderColor: 'border-blue-500/40',
    selectedRing: 'ring-blue-400/40 border-blue-500',
  },
  kbzpay: {
    name: 'KBZPay',
    logo: '/logos/kpay.png',
    color: '#0054A6',
    accentBg: 'bg-blue-500/15',
    borderColor: 'border-blue-500/40',
    selectedRing: 'ring-blue-400/40 border-blue-500',
  },
  direct_kpay: {
    name: 'KBZPay',
    logo: '/logos/kpay.png',
    color: '#0054A6',
    accentBg: 'bg-blue-500/15',
    borderColor: 'border-blue-500/40',
    selectedRing: 'ring-blue-400/40 border-blue-500',
  },
  external_kpay: {
    name: 'KBZPay',
    logo: '/logos/kpay.png',
    color: '#0054A6',
    accentBg: 'bg-blue-500/15',
    borderColor: 'border-blue-500/40',
    selectedRing: 'ring-blue-400/40 border-blue-500',
  },
  wavepay: {
    name: 'WavePay',
    logo: '/logos/wavepay.png',
    color: '#FFD700',
    accentBg: 'bg-amber-500/15',
    borderColor: 'border-amber-500/40',
    selectedRing: 'ring-amber-400/40 border-amber-500',
  },
  direct_wave: {
    name: 'WavePay',
    logo: '/logos/wavepay.png',
    color: '#FFD700',
    accentBg: 'bg-amber-500/15',
    borderColor: 'border-amber-500/40',
    selectedRing: 'ring-amber-400/40 border-amber-500',
  },
  external_wave: {
    name: 'WavePay',
    logo: '/logos/wavepay.png',
    color: '#FFD700',
    accentBg: 'bg-amber-500/15',
    borderColor: 'border-amber-500/40',
    selectedRing: 'ring-amber-400/40 border-amber-500',
  },
};

interface PaymentLogoProps {
  method?: PaymentMethodId | string | null;
  size?: keyof typeof SIZE_MAP;
  className?: string;
  showBorder?: boolean;
}

export const PaymentLogo: React.FC<PaymentLogoProps> = ({
  method,
  size = 'md',
  className = '',
  showBorder = true,
}) => {
  const normKey = (method ? String(method) : 'kpay').toLowerCase();
  const config = PAYMENT_CONFIGS[normKey] || {
    name: method ? String(method) : 'Payment',
    logo: normKey.includes('wave') ? '/logos/wavepay.png' : '/logos/kpay.png',
    color: '#0EA5E9',
    accentBg: 'bg-sky-500/15',
    borderColor: 'border-sky-500/40',
    selectedRing: 'ring-sky-400/30 border-sky-500',
  };

  const sizeClasses = SIZE_MAP[size] || SIZE_MAP.md;

  return (
    <div
      className={`inline-flex items-center justify-center shrink-0 overflow-hidden bg-white ${
        showBorder ? 'border border-plum-border/60 shadow-xs' : ''
      } ${sizeClasses} ${className}`}
    >
      <img
        src={config.logo}
        alt={`${config.name} logo`}
        className="w-full h-full object-contain"
        decoding="async"
        onError={(e) => {
          const target = e.currentTarget;
          target.style.display = 'none';
          if (target.parentElement) {
            target.parentElement.innerText = config.name.slice(0, 3);
          }
        }}
      />
    </div>
  );
};
